import { IconArrowUpRight, IconLink } from "@tabler/icons-react"
import { useState } from "react"

import routes from "@forento/core/routes"

import Button from "~/components/Button"
import { ModalButtons, ModalContentPadding, ModalText, ModalTitle } from "~/components/Modal"
import { useCustomAlert } from "~/contexts/AlertContext"
import { usePlatform } from "~/contexts/UserContext"

type Type = "course" | "downloadable" | "session" | "event"

function getUrlPathByItem(type: Type, id: number): string {
	switch (type) {
		case "course":
			return routes.platform.course.detail(id)
		case "downloadable":
			return routes.platform.downloadable.detail(id)
		case "session":
			return routes.platform.session.detail(id)
		case "event":
			return routes.platform.event.detail(id)
	}
}

export default function useProductPublishedAlert(type: Type) {
	const platform = usePlatform()!

	return useCustomAlert<{ itemId: number }>(({ itemId }) => onClose => (
		<PublishedActions
			type={type}
			url={`https://${platform.domain.platform}${getUrlPathByItem(type, itemId)}`}
			onClose={onClose}
		/>
	))
}

function PublishedActions({ type, url, onClose }: { type: Type; url: string; onClose: () => void }) {
	const [copied, setCopied] = useState(false)
	const [copyError, setCopyError] = useState(false)

	return (
		<>
			<ModalTitle>Published!</ModalTitle>
			<ModalText>Your {type} is live. Share it with your first students to get your launch started.</ModalText>
			<ModalContentPadding className="flex flex-col gap-3">
				<a
					href={url}
					target="_blank"
					rel="noopener noreferrer"
					className="group border-primary/15 bg-primary/5 text-primary hover:border-primary/30 hover:bg-primary/10 focus-visible:outline-primary flex items-center gap-3 rounded-xl border p-4 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2"
				>
					<span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white">
						<IconLink className="size-5" aria-hidden="true" />
					</span>
					<span className="min-w-0 flex-1">
						<span className="text-light block text-xs font-medium">Your live {type}</span>
						<span className="mt-1 block text-sm leading-snug font-semibold break-all">
							{url.replace(/^https:\/\//, "")}
						</span>
					</span>
					<IconArrowUpRight
						className="size-5 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
						aria-hidden="true"
					/>
					<span className="sr-only">(opens in a new tab)</span>
				</a>
				<p role="status" className="text-light text-sm">
					{copyError
						? "Could not copy automatically. Right-click or long-press the link to copy its address."
						: copied
							? "Product link copied. Ready to share!"
							: "Send this link in a personal message or share it with your audience."}
				</p>
			</ModalContentPadding>
			<ModalButtons>
				<Button variant="secondary" onClick={onClose}>
					Close
				</Button>
				<Button variant="secondary" onClick={url} newTab>
					Visit {type}
				</Button>
				<Button
					variant="primary"
					onClick={async () => {
						try {
							await navigator.clipboard.writeText(url)
							setCopied(true)
							setCopyError(false)
						} catch {
							setCopyError(true)
						}
					}}
				>
					Copy product link
				</Button>
			</ModalButtons>
		</>
	)
}
